import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Chip, CircularProgress, Grid, Paper, Skeleton, Stack, Tab, Tabs, Typography } from "@mui/material";
import {
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Percent as PercentIcon,
  Flag as FlagIcon,
  CardGiftcard as BonusIcon,
  TrackChanges as TargetIcon,
  WorkspacePremium as BountyIcon,
  AccountBalance as CommissionIcon,
  AttachMoney as MoneyIcon,
} from "@mui/icons-material";
import { getCounsellorDashboard } from "../Components/dashboardApi";
import DashboardFilters, { getDateRangeFromPreset } from "./dashboard/DashboardFilters";
import MetricCard from "./dashboard/MetricCard";
import styles from "./AllEnquiries.module.scss";
import dashboardStyles from "./dashboard/Dashboard.module.scss";
import { toast } from "react-toastify";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, PieChart, Pie, Cell } from "recharts";

const SourceDistributionChart = lazy(() => import("./dashboard/SourceDistributionChart"));

const ChartSkeleton = () => (
  <Skeleton variant="rounded" height={320} sx={{ borderRadius: 2 }} />
);

function EnquiriesConversionsChart({ trends }) {
  const data = useMemo(() => {
    const enq = trends?.enquiries_over_time ?? [];
    const conv = trends?.conversions_over_time ?? [];
    const dates = new Set([...enq.map((e) => e.date), ...conv.map((c) => c.date)]);
    return Array.from(dates)
      .sort()
      .map((date) => {
        const e = enq.find((x) => x.date === date);
        const c = conv.find((x) => x.date === date);
        return {
          date: date.length > 10 ? date.slice(5) : date,
          enquiries: e?.count ?? 0,
          conversions: c?.count ?? 0,
        };
      });
  }, [trends]);

  if (data.length === 0) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: "14px",
        border: "1px solid var(--border-color, #e5e7eb)",
        boxShadow: "0 2px 8px rgba(17, 24, 39, 0.06)",
        background: "var(--bg-card, #fff)",
        height: "100%",
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2.25, color: "var(--text-primary)" }}>
        Enquiries & Conversions
      </Typography>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #e5e7eb)" />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip
            contentStyle={{
              borderRadius: 10,
              border: "1px solid var(--border-color)",
              boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
            }}
          />
          <Legend />
          <Bar dataKey="enquiries" fill="#5e72e4" name="Enquiries" radius={[4, 4, 0, 0]} />
          <Bar dataKey="conversions" fill="#2dce89" name="Conversions" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
}

const INCENTIVE_COLORS = { Bonus: "#5e72e4", Target: "#2dce89", Bounty: "#fb6340", Commission: "#11cdef" };

function formatOverallTargetCardValue(ot) {
  if (!ot?.assigned) return "No overall target assigned";
  const m = (ot.metric || "").toLowerCase();
  const g = Number(ot.goal);
  const c = Number(ot.current);
  const fmt = (n, isGoal) => {
    if (m === "conversion_rate") return `${Number(n).toFixed(isGoal ? 0 : 1)}%`;
    if (m === "revenue") return Number(n).toLocaleString();
    return String(Math.round(Number(n)));
  };
  const fmtG = fmt(g, true);
  if (ot.complete) return `${fmtG}/${fmtG} · Target complete`;
  return `${fmt(c, false)}/${fmtG}`;
}
const INCENTIVE_KEYS = [
  { key: "Bonus", color: INCENTIVE_COLORS.Bonus },
  { key: "Target", color: INCENTIVE_COLORS.Target },
  { key: "Bounty", color: INCENTIVE_COLORS.Bounty },
  { key: "Commission", color: INCENTIVE_COLORS.Commission },
];

function IncentivesSection({ incentives }) {
  const breakdown = incentives?.earned_breakdown ?? {};
  const next = incentives?.next_milestone ?? {};
  const totalEarned = Number(incentives?.total_earned) || 0;
  const pieData = useMemo(() => {
    const items = [
      { name: "Bonus", value: Number(breakdown.bonus) || 0 },
      { name: "Target", value: Number(breakdown.target) || 0 },
      { name: "Bounty", value: Number(breakdown.bounty) || 0 },
      { name: "Commission", value: Number(breakdown.commission) || 0 },
    ].filter((d) => d.value > 0);
    return items;
  }, [breakdown]);

  const hasPieData = pieData.length > 0;

  const cardStyle = {
    p: 2.5,
    borderRadius: "14px",
    border: "1px solid var(--border-color, #e5e7eb)",
    boxShadow: "0 2px 8px rgba(17, 24, 39, 0.06)",
    background: "var(--bg-card, #fff)",
    height: "100%",
    minHeight: 140,
    transition: "box-shadow 0.2s ease",
    "&:hover": { boxShadow: "0 6px 20px rgba(17, 24, 39, 0.08)" },
  };

  const MilestoneCard = ({ title, items, icon, iconColor }) => {
    const list = Array.isArray(items) ? items : (items?.items ?? []);
    const entries = list
      .map((it) => (typeof it === "string" ? { text: it } : it))
      .filter((it) => it?.text);
    return (
      <Paper elevation={0} sx={cardStyle}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: iconColor ? `${iconColor}18` : "action.hover",
              color: iconColor || "text.secondary",
            }}
          >
            {icon}
          </Box>
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: "var(--text-primary)" }}>
            {title}
          </Typography>
        </Box>
        {entries.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5, pl: 0.5 }}>
            Nothing left to do
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            {entries.map((it, i) => (
              <Paper
                key={i}
                elevation={0}
                sx={{
                  p: 1.25,
                  borderRadius: 2,
                  border: "1px solid var(--border-color, #e5e7eb)",
                  bgcolor: "action.hover",
                  "&:hover": { bgcolor: "action.selected" },
                }}
              >
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <Chip
                    size="small"
                    label="Next"
                    sx={{
                      height: 22,
                      fontWeight: 600,
                      fontSize: "0.7rem",
                      bgcolor: iconColor ? `${iconColor}22` : "primary.light",
                      color: iconColor || "primary.dark",
                    }}
                  />
                  {it.reward && (
                    <Chip
                      size="small"
                      label={it.reward}
                      sx={{
                        height: 22,
                        fontWeight: 600,
                        fontSize: "0.7rem",
                        bgcolor: "action.selected",
                        border: "1px solid",
                        borderColor: "divider",
                        color: "text.primary",
                      }}
                    />
                  )}
                </Box>
                {it.scope_target && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontWeight: 600 }}>
                    Scope: {it.scope_target}
                  </Typography>
                )}
                {it.bounty_name && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontWeight: 600 }}>
                    Bounty: {it.bounty_name}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                  {it.text}
                </Typography>
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>
    );
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <Typography variant="h6" fontWeight={700} sx={{ color: "var(--text-primary)" }}>
          Incentives
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* <MoneyIcon sx={{ color: "success.main", fontSize: 28 }} /> */}
          <Typography variant="h6" fontWeight={700} sx={{ color: "var(--text-primary)" }}>
            Total earned: {totalEarned.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} LKR
          </Typography>
        </Box>
      </Box>
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <MilestoneCard title="Bonus" items={next.bonus?.items} icon={<BonusIcon sx={{ fontSize: 24 }} />} iconColor={INCENTIVE_COLORS.Bonus} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MilestoneCard title="Target" items={next.target?.items} icon={<TargetIcon sx={{ fontSize: 24 }} />} iconColor={INCENTIVE_COLORS.Target} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MilestoneCard title="Bounty" items={next.bounty?.items} icon={<BountyIcon sx={{ fontSize: 24 }} />} iconColor={INCENTIVE_COLORS.Bounty} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MilestoneCard title="Commission" items={next.commission?.items} icon={<CommissionIcon sx={{ fontSize: 24 }} />} iconColor={INCENTIVE_COLORS.Commission} />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: "14px",
              border: "1px solid var(--border-color, #e5e7eb)",
              boxShadow: "0 2px 8px rgba(17, 24, 39, 0.06)",
              background: "var(--bg-card, #fff)",
              height: "100%",
              minHeight: 320,
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1, color: "var(--text-primary)" }}>
              Earned from
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Total incentives this period: {totalEarned.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
            {!hasPieData ? (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">No earnings in this period</Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={64}
                      outerRadius={92}
                      paddingAngle={3}
                    >
                      {pieData.map((entry) => (
                        <Cell key={entry.name} fill={INCENTIVE_COLORS[entry.name] ?? "#8898aa"} stroke="var(--bg-card, #fff)" strokeWidth={2} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [Number(value).toFixed(2), "Earned"]}
                      contentStyle={{ borderRadius: 10, border: "1px solid var(--border-color)", boxShadow: "0 8px 24px rgba(0,0,0,0.1)" }}
                    />
                </PieChart>
                </ResponsiveContainer>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, justifyContent: "center", mt: 1 }}>
                  {INCENTIVE_KEYS.map(({ key, color }) => (
                    <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: color }} />
                      <Typography variant="caption" fontWeight={600} sx={{ color: "var(--text-secondary)" }}>
                        {key}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

function FollowUpCard({ follow_ups, onNavigate }) {
  const todayStr = new Date().toISOString().slice(0, 10);
  const overdue = follow_ups?.filter((f) => f.is_overdue) ?? [];
  const dueToday = follow_ups?.filter((f) => f.next_follow_up === todayStr) ?? [];
  const upcoming = follow_ups?.filter((f) => !f.is_overdue && f.next_follow_up !== todayStr) ?? [];
  const rows = [...overdue, ...dueToday, ...upcoming.slice(0, 12)];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: "14px",
        border: "1px solid var(--border-color, #e5e7eb)",
        boxShadow: "0 2px 8px rgba(17, 24, 39, 0.06)",
        background: "var(--bg-card, #fff)",
        height: "100%",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={600} color="var(--text-primary)">Follow-ups</Typography>
        {overdue.length > 0 && (
          <Typography component="span" variant="caption" sx={{ color: "error.main", fontWeight: 600 }}>{overdue.length} overdue</Typography>
        )}
      </Box>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No follow-ups</Typography>
      ) : (
        <Stack spacing={0.5}>
          {rows.map((f, i) => (
            <Box
              key={i}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                py: 1,
                px: 1.5,
                borderRadius: 1,
                bgcolor: f.is_overdue ? "error.light" : f.next_follow_up === todayStr ? "warning.light" : "action.hover",
                "&:hover": { bgcolor: "action.selected" },
              }}
            >
              <Box>
                <Typography variant="body2" fontWeight={600}>{f.student_name}</Typography>
                <Typography variant="caption" color="text.secondary">{f.status || "—"} · Next: {f.next_follow_up ?? "—"}</Typography>
              </Box>
              <Button size="small" variant="outlined" onClick={() => onNavigate("/enquiry_profile", { state: { enquiry: { id: f.id } } })}>View</Button>
            </Box>
          ))}
        </Stack>
      )}
    </Paper>
  );
}

function TasksCard({ tasks, onNavigate }) {
  const todayStr = new Date().toISOString().slice(0, 10);
  const byDue = useMemo(() => {
    const today = []; const upcoming = []; const overdue = [];
    (tasks ?? []).forEach((t) => {
      const d = t.due_date?.slice(0, 10);
      if (!d) { upcoming.push(t); return; }
      if (t.priority === "high" || t.is_overdue) overdue.push(t);
      else if (d === todayStr) today.push(t);
      else upcoming.push(t);
    });
    return { today, upcoming, overdue };
  }, [tasks, todayStr]);

  const openTask = (t) => {
    if (t.id?.startsWith("appointment-")) {
      onNavigate("/appointments/my");
      return;
    }
    const id = t.related_enquiry || String(t.id).replace("appointment-", "");
    if (id) onNavigate("/enquiry_profile", { state: { enquiry: { id: Number(id) || id } } });
  };

  const all = [...byDue.overdue, ...byDue.today, ...byDue.upcoming].slice(0, 10);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: "14px",
        border: "1px solid var(--border-color, #e5e7eb)",
        boxShadow: "0 2px 8px rgba(17, 24, 39, 0.06)",
        background: "var(--bg-card, #fff)",
        height: "100%",
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5, color: "var(--text-primary)" }}>Tasks</Typography>
      {all.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No tasks</Typography>
      ) : (
        <Stack spacing={0.5}>
          {all.map((t, i) => (
            <Box key={i} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1, px: 1.5, borderRadius: 1, bgcolor: "action.hover" }}>
              <Typography variant="body2">{t.title}</Typography>
              <Button size="small" variant="outlined" onClick={() => openTask(t)}>Open</Button>
            </Box>
          ))}
        </Stack>
      )}
    </Paper>
  );
}

function AlertsInsightsCard({ alerts, insights }) {
  const al = alerts ?? [];
  const inList = insights ?? [];

  return (
    <Stack spacing={2}>
      {al.length > 0 && (
        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "warning.main", bgcolor: "warning.light" }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Alerts</Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>{al.map((a, i) => <li key={i}>{a}</li>)}</ul>
        </Paper>
      )}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: "14px",
          border: "1px solid var(--border-color, #e5e7eb)",
          boxShadow: "0 2px 8px rgba(17, 24, 39, 0.06)",
          background: "var(--bg-card, #fff)",
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5, color: "var(--text-primary)" }}>Insights</Typography>
        {inList.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No insights</Typography>
        ) : (
          <ul style={{ margin: 0, paddingLeft: 20 }}>{inList.map((s, i) => <li key={i}>{s}</li>)}</ul>
        )}
      </Paper>
    </Stack>
  );
}

export default function CounsellorDashboard() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(() => getDateRangeFromPreset("Monthly"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [incentivesBusy, setIncentivesBusy] = useState(false);
  const [error, setError] = useState(null);
  const [mainTab, setMainTab] = useState(0);
  const dataRef = useRef(null);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const hasDates = filters?.start_date && filters?.end_date;

  const load = useCallback(
    async (signal) => {
      if (!hasDates) return;
      const includeIncentives = mainTab === 1;
      const hadData = dataRef.current != null;
      setError(null);
      if (!hadData) {
        setLoading(true);
      } else if (includeIncentives) {
        setIncentivesBusy(true);
      } else {
        setLoading(true);
      }
      try {
        const out = await getCounsellorDashboard(
          {
            start_date: filters.start_date,
            end_date: filters.end_date,
            include_incentives: includeIncentives,
          },
          signal
        );
        if (signal?.aborted) return;
        setData(out);
      } catch (e) {
        if (e?.name === "AbortError") return;
        setError(e?.message || "Failed to load dashboard");
        toast.error(e?.message || "Failed to load dashboard");
      } finally {
        if (!signal?.aborted) {
          setLoading(false);
          setIncentivesBusy(false);
        }
      }
    },
    [hasDates, filters?.start_date, filters?.end_date, mainTab]
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const handleNavigate = useCallback((path, state) => {
    navigate(path, state || {});
  }, [navigate]);

  const overview = data?.overview;
  const targets = data?.targets;
  const performance = data?.performance;
  const trends = data?.trends;
  const overallTarget = targets?.overall_target;
  const targetProgressDisplay = formatOverallTargetCardValue(overallTarget);
  const sourcesForChart = (performance?.sources ?? []).map((s) => ({
    source: s.source ?? "Unknown",
    enquiry_count: s.enquiry_count ?? 0,
  }));

  return (
    <Box className={`${styles.root} ${dashboardStyles.dashboardContent}`}>
      <Paper elevation={0} className={`${styles.contentPaper} ${dashboardStyles.contentPaper}`}>
        <Stack spacing={3}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: "14px",
              border: "1px solid var(--border-color, #e5e7eb)",
              boxShadow: "0 2px 8px rgba(17, 24, 39, 0.06)",
              background: "var(--bg-card, #fff)",
            }}
          >
            <DashboardFilters value={filters} onChange={setFilters} hideCounsellor />
          </Paper>

          <Paper
            elevation={0}
            sx={{
              px: 1.5,
              borderRadius: "14px",
              border: "1px solid var(--border-color, #e5e7eb)",
              boxShadow: "0 2px 8px rgba(17, 24, 39, 0.06)",
              background: "var(--bg-card, #fff)",
            }}
          >
            <Tabs
              value={mainTab}
              onChange={(_, v) => setMainTab(v)}
              sx={{
                minHeight: 44,
                "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minHeight: 44 },
              }}
            >
              <Tab label="Overview" />
              <Tab label="Incentives" />
            </Tabs>
          </Paper>

          {loading && !data ? (
            <Box sx={{ py: 2 }}>
              <Skeleton variant="rounded" height={100} sx={{ mb: 2.5, borderRadius: 2 }} />
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", width: "100%" }}>
                {[...Array(4)].map((_, i) => (
                  <Box key={i} sx={{ flex: "1 1 0", minWidth: 120 }}>
                    <Skeleton variant="rounded" height={120} sx={{ borderRadius: 2 }} />
                  </Box>
                ))}
              </Box>
            </Box>
          ) : error && !data ? (
            <Paper sx={{ p: 3, textAlign: "center" }}>
              <Typography color="error">{error}</Typography>
              <Button variant="contained" size="small" sx={{ mt: 2 }} onClick={() => load(null)}>Retry</Button>
            </Paper>
          ) : (
            <>
              {mainTab === 0 && (
                <>
                  <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", width: "100%" }}>
                    <Box sx={{ flex: "1 1 0", minWidth: 120 }}>
                      <MetricCard
                        label="Total Enquiries"
                        value={overview?.total_enquiries ?? 0}
                        icon={<PeopleIcon />}
                        color="#5e72e4"
                        tooltip="Enquiries received (created) in the selected period"
                      />
                    </Box>
                    <Box sx={{ flex: "1 1 0", minWidth: 120 }}>
                      <MetricCard
                        label="Conversions"
                        value={overview?.total_conversions ?? 0}
                        icon={<TrendingUpIcon />}
                        color="#2dce89"
                        tooltip="Enquiries that converted in the selected period"
                      />
                    </Box>
                    <Box sx={{ flex: "1 1 0", minWidth: 120 }}>
                      <MetricCard
                        label="Conversion Rate"
                        value={overview?.conversion_rate != null ? `${overview.conversion_rate}%` : "0%"}
                        icon={<PercentIcon />}
                        color="#8965e0"
                        tooltip="Of enquiries received in this period, how many converted (converted leads ÷ enquiries received)"
                      />
                    </Box>
                    <Box sx={{ flex: "1 1 0", minWidth: 120 }}>
                      <MetricCard
                        label="Overall target"
                        value={targetProgressDisplay}
                        icon={<FlagIcon />}
                        color="#11cdef"
                        countUp={false}
                        to={overallTarget?.assigned && overallTarget?.target_id ? `/targets/${overallTarget.target_id}` : undefined}
                        tooltip="Your overall target progress for the assigned target"
                      />
                    </Box>
                  </Box>

                  <Grid container spacing={2.5}>
                    <Grid item xs={12} lg={5}>
                      <Suspense fallback={<ChartSkeleton />}>
                        <SourceDistributionChart data={sourcesForChart} loading={false} />
                      </Suspense>
                    </Grid>
                    <Grid item xs={12} lg={7}>
                      <EnquiriesConversionsChart trends={trends} />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2.5}>
                    <Grid item xs={12} lg={7}>
                      <FollowUpCard follow_ups={data?.follow_ups} onNavigate={handleNavigate} />
                    </Grid>
                    <Grid item xs={12} lg={5}>
                      <TasksCard tasks={data?.tasks} onNavigate={handleNavigate} />
                    </Grid>
                  </Grid>

                  <AlertsInsightsCard alerts={data?.alerts} insights={data?.insights} />
                </>
              )}

              {mainTab === 1 &&
                (incentivesBusy ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <IncentivesSection incentives={data?.incentives} />
                ))}
            </>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
